import type { BuildingGraph } from "building-types/building/buildingGraphTypes";
import type { Building } from "building-types/building/buildingTypes";
import type {
  BuildingGraphFaceSideHandle,
  BuildingGraphFaceSideDirection,
} from "building-types/buildingGraph/buildingGraphSideHandles";
import type { BuildingGraphFilterBuilding } from "building-types/buildingGraphFilter/buildingGraphFilterBuilding";
import type { WallPlaneFilterMatches } from "building-types/buildingGraphFilter/wallPlaneFilterMatches";
import type { CipComponentEntity } from "building-types/floors/cipComponentTypes";
import type { MasonryComponentEntity } from "building-types/floors/masonryComponentTypes";
import type { SheathingComponentEntity } from "building-types/floors/sheathingComponentTypes";
import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type { WallPlaneRuleHandle } from "building-types/handles/docEntityHandleTypes";
import type { FormulaDisabled } from "building-types/options/formulaDisabled";
import type {
  WallPlaneMasses,
  WallPlane,
  WallPlaneDomainOperation,
  WallPlaneSubdomain,
  WallPlaneSubdomainMiterEdge,
  WallPlaneSubdomainMiterMass,
} from "building-types/walls2/wallPlane";
import type { WallPlaneAssemblyEntity } from "building-types/walls2/wallPlaneAssemblyTypes";
import type { DocWallPlaneFilter } from "building-types/walls2/wallPlaneFilters";
import { enumerateWallPlaneOperators } from "building-types/walls2/wallPlaneOperations";
import {
  isWallFaceRule,
  isWallPlaneAnchoredRule,
  type WallPlaneRuleEntity,
} from "building-types/walls2/wallPlaneRules";
import type { Table, RWTable } from "common/data/table";
import { builtInWallPlaneAssemblies } from "doc/defaults/defaultWallPlaneAssembly";
import { evalFormulas } from "doc/formula/evalFormulas";
import { makeXYPlane } from "doc/measurementUtils";
import { BuildingGenerationContext } from "doc/utils/buildingGenerationContext";
import { aabb2Copy, aabb2Grow, transformAABB2Basis } from "geom/aabb2";
import { DIST_EPSILON, DIST_EPSILON_SQUARED } from "geom/core";
import { hashVec2 } from "geom/hash";
import { line2Side, line2Tangent } from "geom/line2";
import { planeNormal, planeEval, planeIsVertical, polyLine2OnPlane } from "geom/plane";
import { vec2Normal } from "geom/polyclip/vec2";
import { polyLine2ReverseCopy } from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { polyLine3AABB3 } from "geom/polyLine3";
import { vec2DistSquared, vec2Dot } from "geom/vec2";
import { vec3Dot } from "geom/vec3";
import { faceSideHandleGetPlaneHashes, resolveBuildingGraphFaceSide } from "../buildingGraph/resolveFaceSide";
import { executeWallFaceRule } from "../buildingGraphFilter/wallFaceRuleExecution";
import { executeWallPlaneAnchoredRule } from "../buildingGraphFilter/wallPlaneFilterExecution";
import { executeWallPlaneFilterOperationFromHandles } from "../buildingGraphFilter/wallPlaneOperationResolution";
import { WallPlaneDescriptorBuilder } from "../elements/walls2/wallPlaneDescriptor";
import {
  defaultWallPlaneFilters,
  defaultWallPlaneRulesExcludingDocOverrides,
} from "../elements/walls2/wallPlaneRuleDefaults";
import { subdomainHasFreeformWallParent } from "../elements/walls2/wallPlaneSubdomainRoomFilter";
import { distance, distanceToSegment, projectOntoSegment, type Vector2 } from "../miterSkeleton/geometry";
import type { MiterSkeletonEdge } from "../miterSkeleton/MiterSkeletonEdge";
import { MiterSkeletonGraph } from "../miterSkeleton/MiterSkeletonGraph";
import type { MiterSourceEdge } from "../miterSkeleton/MiterSourceEdge";
import { MiterSourceGraph } from "../miterSkeleton/MiterSourceGraph";
import type { MiterSourceNode } from "../miterSkeleton/MiterSourceNode";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

function select({
  buildingGraph,
  rooms,
  doc,
  resolvedDatums,
  datumAbstractions,
  wallPlaneFilterMatches,
  optionChoices,
  entityDisabledByGroup,
  roomFloorAssemblies,
  settings,
}: Building) {
  const {
    wallPlaneRules,
    wallPlaneAssemblies,
    framingComponents,
    masonryComponents,
    cipComponents,
    sheathingComponents,
  } = doc;
  return {
    buildingGraph,
    rooms,
    resolvedDatums,
    datumAbstractions,
    wallPlaneRules,
    wallPlaneFilters: doc.wallPlaneFilters,
    wallPlaneFilterMatches,
    optionChoices,
    entityDisabledByGroup,
    wallPlaneAssemblies,
    framingComponents,
    masonryComponents,
    cipComponents,
    sheathingComponents,
    roomFloorAssemblies,
    settings,
    staticRooms: doc.rooms,
  };
}

const compare = makeGenerationStageComparator<typeof select>({});

function generate({
  buildingGraph,
  rooms,
  resolvedDatums,
  datumAbstractions,
  wallPlaneRules,
  wallPlaneAssemblies,
  wallPlaneFilters,
  wallPlaneFilterMatches,
  optionChoices,
  entityDisabledByGroup,
  framingComponents,
  masonryComponents,
  cipComponents,
  sheathingComponents,
  staticRooms,
}: ReturnType<typeof select>): Pick<Building, "wallPlaneMasses" | "wallPlanes"> {
  const resolvedDocWallPlaneRules = resolveWallPlaneRules(wallPlaneRules, optionChoices, entityDisabledByGroup);

  const resolvedWallPlaneFilters = { ...defaultWallPlaneFilters, ...wallPlaneFilters };
  const resolvedWallPlaneAssemblies = { ...builtInWallPlaneAssemblies, ...wallPlaneAssemblies };
  const wallPlaneRulesBySortOrder = [
    ...defaultWallPlaneRulesExcludingDocOverrides(wallPlaneRules),
    ...resolvedDocWallPlaneRules,
  ].sort((a, b) => a.sortIndex - b.sortIndex || a.id.localeCompare(b.id));
  const wallPlaneDomainOperations = collectWallPlaneDomainOperations(
    buildingViewForQuery(buildingGraph, rooms, staticRooms),
    wallPlaneRulesBySortOrder,
    resolvedWallPlaneFilters,
    wallPlaneFilterMatches ?? {},
  );

  const wallPlanes = computeWallPlanes(
    buildingGraph,
    wallPlaneDomainOperations,
    resolvedDatums,
    datumAbstractions,
    resolvedWallPlaneAssemblies,
    framingComponents,
    masonryComponents,
    cipComponents,
    sheathingComponents,
  );

  const miters = computeWallPlaneSubdomainMiterEdges(buildingGraph, wallPlanes);
  const { masses, grids: massGrids } = computeWallPlaneSubdomainMiterMasses(buildingGraph, wallPlanes, miters);
  const wallPlaneMasses: WallPlaneMasses = {
    miters,
    masses,
    massGrids,
  };

  return { wallPlaneMasses, wallPlanes };
}

export default memo(select, generate, compare);

function resolveWallPlaneRules(
  wallPlaneRules: Table<WallPlaneRuleEntity>,
  optionChoices: Building["optionChoices"],
  entityDisabledByGroup: Building["entityDisabledByGroup"],
): WallPlaneRuleEntity[] {
  const evaluatedWallPlaneRules = evalFormulas(
    wallPlaneRules,
    (v) => optionChoices[v],
    undefined,
    entityDisabledByGroup,
  ) as Table<WallPlaneRuleEntity & FormulaDisabled>;

  return Object.values(evaluatedWallPlaneRules)
    .filter((wallPlaneRule) => !wallPlaneRule.disabled)
    .sort((a, b) => a.sortIndex - b.sortIndex || a.id.localeCompare(b.id));
}

function collectWallPlaneDomainOperations(
  building: BuildingGraphFilterBuilding,
  wallPlaneRules: WallPlaneRuleEntity[],
  wallPlaneFilters: Table<DocWallPlaneFilter>,
  wallPlaneFilterMatches: WallPlaneFilterMatches,
) {
  const wallPlaneDomainOperations: Record<string, WallPlaneDomainOperation[]> = {};

  function addWallPlaneDomainOperation(wallPlaneHash: string, operation: WallPlaneDomainOperation) {
    const operations = wallPlaneDomainOperations[wallPlaneHash] ?? [];
    operations.push(operation);
    wallPlaneDomainOperations[wallPlaneHash] = operations;
  }

  for (const rule of wallPlaneRules) {
    const sideHandles = collectRuleSideHandles(rule, wallPlaneFilters, building, wallPlaneFilterMatches);
    if (sideHandles.length === 0) {
      continue;
    }

    if (!rule.operation) {
      continue;
    }

    const ruleHandle: WallPlaneRuleHandle = {
      type: "WALL_PLANE_RULE",
      id: rule.id,
    };

    for (const wallPlaneOperator of enumerateWallPlaneOperators(rule.operation)) {
      for (const { handle, operator, resolvedFaceSide } of executeWallPlaneFilterOperationFromHandles(
        sideHandles,
        wallPlaneOperator,
      )) {
        const wallPlaneDomainOperation: WallPlaneDomainOperation = {
          rule: ruleHandle,
          source: ruleHandle,
          wallPlaneOperator: operator,
          wallPlaneFaceSideHandle: handle,
          operationBoundary: rule.operationBoundary,
          resolvedFaceSide,
        };
        for (const planeHash of faceSideHandleGetPlaneHashes(handle, building)) {
          addWallPlaneDomainOperation(planeHash, wallPlaneDomainOperation);
        }
      }
    }
  }

  return wallPlaneDomainOperations;
}

interface RuleSideHandleResult {
  readonly handle: BuildingGraphFaceSideHandle;
  readonly resolvedCanonicalDirectionInPlane?: BuildingGraphFaceSideDirection;
}

function collectRuleSideHandles(
  rule: WallPlaneRuleEntity,
  wallPlaneFilters: Table<DocWallPlaneFilter>,
  building: BuildingGraphFilterBuilding,
  wallPlaneFilterMatches: WallPlaneFilterMatches,
): readonly RuleSideHandleResult[] {
  if (isWallPlaneAnchoredRule(rule)) {
    return [...executeWallPlaneAnchoredRule(rule)].map((handle) => {
      const resolved = resolveBuildingGraphFaceSide(handle, building);
      return {
        handle,
        resolvedCanonicalDirectionInPlane: resolved?.resolvedCanonicalDirectionInPlane,
      };
    });
  }
  if (isWallFaceRule(rule)) {
    return [...executeWallFaceRule(rule, wallPlaneFilters, building, wallPlaneFilterMatches)].map((handle) => {
      const resolved = resolveBuildingGraphFaceSide(handle, building);
      return {
        handle,
        resolvedCanonicalDirectionInPlane: resolved?.resolvedCanonicalDirectionInPlane,
      };
    });
  }
  return [];
}

/**
 * Builds a minimal query view carrying only the fields required by the
 * building-graph query engine: the generated rooms plus a selective doc holding just
 * the room table the engine falls back to.
 */
function buildingViewForQuery(
  buildingGraph: BuildingGraph,
  rooms: Building["rooms"],
  staticRooms: Building["doc"]["rooms"],
): BuildingGraphFilterBuilding {
  return {
    buildingGraph,
    rooms,
    doc: { rooms: staticRooms },
  };
}

function computeWallPlanes(
  buildingGraph: BuildingGraph,
  wallPlaneDomainOperations: Record<string, WallPlaneDomainOperation[]>,
  resolvedDatums: Building["resolvedDatums"],
  datumAbstractions: Building["datumAbstractions"],
  wallPlaneAssemblies: Table<WallPlaneAssemblyEntity>,
  framingComponents: Table<FramingComponentEntity>,
  masonryComponents: Table<MasonryComponentEntity>,
  cipComponents: Table<CipComponentEntity>,
  sheathingComponents: Table<SheathingComponentEntity>,
): Table<WallPlane> {
  const wallPlanes: RWTable<WallPlane> = {};
  const verticalBuildingGraphPlanes = Object.values(buildingGraph.buildingPlanes).filter((buildingPlane) =>
    planeIsVertical(buildingPlane.planeFace.plane),
  );

  for (const buildingPlane of verticalBuildingGraphPlanes) {
    const { planeHash: wallPlaneHash, planeFace: wallPlaneFace } = buildingPlane;
    const generatedHandle = {
      type: "GENERATED_ENTITY" as const,
      entity: {
        entityType: "WALL_PLANE" as const,
        id: wallPlaneHash,
      },
    };

    const boundary3 = polyLine2OnPlane(wallPlaneFace.plane, wallPlaneFace.loops[0]);
    BuildingGenerationContext.indexAddNode(generatedHandle, polyLine3AABB3(boundary3));

    const operations = wallPlaneDomainOperations[wallPlaneHash] ?? [];
    const wallPlaneDescriptorBuilder = new WallPlaneDescriptorBuilder(
      buildingGraph,
      buildingPlane,
      resolvedDatums,
      datumAbstractions,
    );

    for (const operation of operations) {
      wallPlaneDescriptorBuilder.addOperation(operation);
    }

    const descriptor = wallPlaneDescriptorBuilder.build(
      wallPlaneAssemblies,
      framingComponents,
      masonryComponents,
      cipComponents,
      sheathingComponents,
    );
    wallPlanes[wallPlaneHash] = {
      entityType: "WALL_PLANE",
      id: wallPlaneHash,
      pickHandle: generatedHandle,
      docEntityHandle: undefined,
      planeHash: wallPlaneHash,
      operations,
      descriptor,
    };
  }

  return wallPlanes;
}

function computeWallPlaneSubdomainMiterEdges(buildingGraph: BuildingGraph, wallPlanes: Table<WallPlane>) {
  const wallPlaneSubdomainMiterEdges: Record<string, WallPlaneSubdomainMiterEdge[]> = {};
  const wallPlaneHashes = Object.keys(wallPlanes);

  for (let i = 0; i < wallPlaneHashes.length; i++) {
    const wallPlaneHash = wallPlaneHashes[i];
    const wallPlane = wallPlanes[wallPlaneHash];
    const wallBuildingPlane = buildingGraph.buildingPlanes[wallPlaneHash];
    const wallPlanePlane = wallBuildingPlane.planeFace.plane;
    const wallPlaneDescriptor = wallPlane.descriptor;
    const wallPlaneAABB = wallPlaneDescriptor.subdomainAABB;
    const wallPlaneMiterEdges: WallPlaneSubdomainMiterEdge[] = [];

    const wallPlaneSearchEpsilon =
      Math.max(Math.abs(wallPlaneDescriptor.subdomainMinW), Math.abs(wallPlaneDescriptor.subdomainMaxW)) / 2 +
      DIST_EPSILON;

    const [wNx, wNy, wNz] = planeNormal(wallPlanePlane);
    const [wSx, wSy] = planeEval(wallPlanePlane, wallPlaneAABB.minX, wallPlaneAABB.minY);
    const [wEx, wEy] = planeEval(wallPlanePlane, wallPlaneAABB.maxX, wallPlaneAABB.maxY);

    // for each other wall plane, check if it intersects the wall plane
    // and if the domain of the other wall plane intersects the wall plane domain
    for (let j = 0; j < wallPlaneHashes.length; j++) {
      const otherWallPlaneHash = wallPlaneHashes[j];
      if (otherWallPlaneHash === wallPlaneHash) {
        continue;
      }

      const otherWallPlane = wallPlanes[otherWallPlaneHash];
      const otherWallBuildingPlane = buildingGraph.buildingPlanes[otherWallPlaneHash];
      const otherWallPlanePlane = otherWallBuildingPlane.planeFace.plane;
      const otherWallPlaneDescriptor = otherWallPlane.descriptor;
      const otherWallPlaneAABB = otherWallPlaneDescriptor.subdomainAABB;

      const [oNx, oNy, oNz] = planeNormal(otherWallPlanePlane);

      const absDot = Math.abs(vec3Dot(oNx, oNy, oNz, wNx, wNy, wNz));
      if (absDot > 1 - DIST_EPSILON) {
        // parallel planes, skip
        continue;
      }

      const [oSx, oSy] = planeEval(otherWallPlanePlane, otherWallPlaneAABB.minX, otherWallPlaneAABB.minY);
      const [oEx, oEy] = planeEval(otherWallPlanePlane, otherWallPlaneAABB.maxX, otherWallPlaneAABB.maxY);

      const oStartSide = line2Side(oSx, oSy, oEx, oEy, wSx, wSy);
      const oEndSide = line2Side(oSx, oSy, oEx, oEy, wEx, wEy);
      if (oStartSide === oEndSide) {
        // no intersection, skip
        continue;
      }

      const projAABB = aabb2Copy(wallPlaneAABB);
      transformAABB2Basis(projAABB, wallPlanePlane, otherWallPlanePlane);
      aabb2Grow(projAABB, wallPlaneSearchEpsilon, wallPlaneSearchEpsilon);

      const projDomains = otherWallPlaneDescriptor.subdomainIndex.search(projAABB);

      // for each projected domain, get the bottom datum in world z and create the
      // line segment of the domain in world space
      for (let domainIndex = 0; domainIndex < projDomains.length; domainIndex++) {
        const subdomain = projDomains[domainIndex];

        // skip if the subdomain has no wall info - i.e it's a gap
        if (!subdomain.wallInfo) {
          continue;
        }

        const sU = subdomain.minX;
        const sV = subdomain.minY;
        const eU = subdomain.maxX;
        const eV = subdomain.maxY;

        const [sx, sy, sz] = planeEval(otherWallPlanePlane, sU, sV);
        const [ex, ey, ez] = planeEval(otherWallPlanePlane, eU, eV);

        const { leftCladdingOffset, leftCoreOffset, rightCoreOffset, rightCladdingOffset } =
          computeWallPlaneSubdomainMiterEdgeOffsets(subdomain);

        const bottom = Math.min(sz, ez);
        const top = Math.max(sz, ez);

        const datum = (bottom + top) / 2;
        const plane = makeXYPlane(0, 0, datum);

        // domain edge in the world xy plane
        const pl = polyLine2New([sx, sy, ex, ey]);
        wallPlaneMiterEdges.push({
          wallPlaneHash: otherWallPlaneHash,
          subdomain,
          edge: pl,
          plane,
          bottomOffset: bottom,
          topOffset: top,
          leftCladdingOffset,
          leftCoreOffset,
          rightCoreOffset,
          rightCladdingOffset,
        });
      }
    }

    // add this wall plane's own subdomains as self-miter edges, alongside the
    // cross-plane subdomains collected above
    for (const subdomain of wallPlaneDescriptor.subdomainIndex.all()) {
      if (!subdomain.wallInfo) {
        continue;
      }

      const sU = subdomain.minX;
      const sV = subdomain.minY;
      const eU = subdomain.maxX;
      const eV = subdomain.maxY;
      const [sx, sy, sz] = planeEval(wallPlanePlane, sU, sV);
      const [ex, ey, ez] = planeEval(wallPlanePlane, eU, eV);

      const { leftCladdingOffset, leftCoreOffset, rightCoreOffset, rightCladdingOffset } =
        computeWallPlaneSubdomainMiterEdgeOffsets(subdomain);

      const bottom = Math.min(sz, ez);
      const top = Math.max(sz, ez);
      const datum = (bottom + top) / 2;

      const pl = polyLine2New([sx, sy, ex, ey]);
      const plane = makeXYPlane(0, 0, datum);
      wallPlaneMiterEdges.push({
        wallPlaneHash,
        subdomain,
        edge: pl,
        plane,
        bottomOffset: bottom,
        topOffset: top,
        leftCladdingOffset,
        leftCoreOffset,
        rightCoreOffset,
        rightCladdingOffset,
      });
    }

    // collect the unique datums for the wall plane miters
    const wallPlaneMiterDatums = new Set<number>();
    for (const miter of wallPlaneMiterEdges) {
      wallPlaneMiterDatums.add(miter.bottomOffset);
      wallPlaneMiterDatums.add(miter.topOffset);
    }

    // create the wall plane core miters by creating subdomains at the unique datums
    const wallPlaneCoreMiters: WallPlaneSubdomainMiterEdge[] = [];
    const wallPlanCoreMiterDatumsArray = Array.from(wallPlaneMiterDatums).sort((a, b) => a - b);
    for (let i = 0; i < wallPlanCoreMiterDatumsArray.length - 1; i++) {
      const minDatum = wallPlanCoreMiterDatumsArray[i];
      const maxDatum = wallPlanCoreMiterDatumsArray[i + 1];
      // const domainDatum = (minDatum + maxDatum) / 2;
      const domainPlane = makeXYPlane(0, 0, minDatum);
      const domainOverlaps = wallPlaneMiterEdges.filter((m) => m.bottomOffset < maxDatum && m.topOffset > minDatum);
      for (const overlap of domainOverlaps) {
        const {
          leftCoreOffset,
          rightCoreOffset,
          leftCladdingOffset,
          rightCladdingOffset,
          edge,
          subdomain,
          wallPlaneHash,
        } = overlap;

        wallPlaneCoreMiters.push({
          plane: domainPlane,
          bottomOffset: minDatum,
          topOffset: maxDatum,
          wallPlaneHash,
          subdomain,
          edge,
          leftCoreOffset,
          rightCoreOffset,
          leftCladdingOffset,
          rightCladdingOffset,
        });
      }
    }

    wallPlaneSubdomainMiterEdges[wallPlaneHash] = wallPlaneCoreMiters;
  }

  return wallPlaneSubdomainMiterEdges;
}

function computeWallPlaneSubdomainMiterEdgeOffsets(subdomain: WallPlaneSubdomain) {
  if (!subdomain.wallInfo) {
    return {
      leftCladdingOffset: 0,
      leftCoreOffset: 0,
      rightCoreOffset: 0,
      rightCladdingOffset: 0,
    };
  }

  const {
    coreThickness,
    wallOffset,
    leftCoreToCladdingAirGap,
    leftCladdingThickness,
    rightCoreToCladdingAirGap,
    rightCladdingThickness,
  } = subdomain.wallInfo;
  const halfCoreThickness = coreThickness / 2;

  const leftCoreOffset = halfCoreThickness - wallOffset;
  const rightCoreOffset = halfCoreThickness + wallOffset;

  const leftCladdingOffset = leftCoreOffset + leftCoreToCladdingAirGap + leftCladdingThickness;
  const rightCladdingOffset = rightCoreOffset + rightCoreToCladdingAirGap + rightCladdingThickness;

  return {
    leftCladdingOffset,
    leftCoreOffset,
    rightCoreOffset,
    rightCladdingOffset,
  };
}

function computeWallPlaneMassOffsets(input: WallPlaneSubdomainMiterEdge) {
  return {
    leftOffset: input.leftCoreOffset,
    rightOffset: input.rightCoreOffset,
  };
}

function computeWallCladdingOffsets(input: WallPlaneSubdomainMiterEdge) {
  return {
    leftOffset: input.leftCladdingOffset,
    rightOffset: input.rightCladdingOffset,
  };
}

function computeWallPlaneSubdomainMiterMasses(
  buildingGraph: BuildingGraph,
  _wallPlanes: Table<WallPlane>,
  miters: Record<string, WallPlaneSubdomainMiterEdge[]>,
): { masses: WallPlaneMasses["masses"]; grids: WallPlaneMasses["massGrids"] } {
  const wallPlaneSubdomainMiterMasses: WallPlaneMasses["masses"] = [];
  const massGrids: WallPlaneMasses["massGrids"] = {};

  for (const wallPlaneHash in miters) {
    const wallPlaneMiters = miters[wallPlaneHash];
    const wallPlaneDatums = new Set<number>();
    const wallPlaneUBounds = new Set<number>();
    for (const miter of wallPlaneMiters) {
      wallPlaneDatums.add(miter.bottomOffset);
      wallPlaneDatums.add(miter.topOffset);
      wallPlaneUBounds.add(miter.subdomain.minX);
      wallPlaneUBounds.add(miter.subdomain.maxX);
    }

    const wallPlaneDatumArray = Array.from(wallPlaneDatums).sort((a, b) => a - b);
    const planeGrid: (WallPlaneSubdomainMiterMass | undefined)[][] = new Array(
      Math.max(wallPlaneDatumArray.length - 1, 0),
    );

    for (let i = 0; i < wallPlaneDatumArray.length - 1; i++) {
      const minDatum = wallPlaneDatumArray[i];
      const maxDatum = wallPlaneDatumArray[i + 1];

      // skip if the domain is too small
      if (Math.abs(maxDatum - minDatum) < DIST_EPSILON) {
        continue;
      }

      // only strict overlap
      const domainOverlaps = wallPlaneMiters.filter((m) => m.bottomOffset < maxDatum && m.topOffset > minDatum);
      const domainPlane = makeXYPlane(0, 0, minDatum);

      const domainCoreMiterResult = miterSubdomainEdges(domainOverlaps, computeWallPlaneMassOffsets, buildingGraph);
      const domainCladdingMiterResult = miterSubdomainEdges(domainOverlaps, computeWallCladdingOffsets, buildingGraph);

      const gridRowCells: (WallPlaneSubdomainMiterMass | undefined)[] = new Array(domainOverlaps.length).fill(
        undefined,
      );
      for (const domain of domainOverlaps) {
        if (domain.wallPlaneHash !== wallPlaneHash) {
          continue;
        }

        const coreHalves = domainCoreMiterResult.get(domain) ?? [];
        const claddingHalves = domainCladdingMiterResult.get(domain) ?? [];
        if (coreHalves.length === 0 || claddingHalves.length === 0) {
          continue;
        }

        // T-splits emit one half per source-edge fragment. Pair by param along the
        // centerline so core/cladding halves stay aligned when graphs are rebuilt
        // separately for each offset predicate.
        const halfCount = Math.min(coreHalves.length, claddingHalves.length);
        for (let halfIndex = 0; halfIndex < halfCount; halfIndex++) {
          const coreResult = coreHalves[halfIndex];
          const claddingResult = claddingHalves[halfIndex];
          const { leftEdge: leftCoreEdge, rightEdge: rightCoreEdge } = coreResult;
          const { leftEdge: leftCladdingEdge, rightEdge: rightCladdingEdge } = claddingResult;

          if (!leftCoreEdge || !rightCoreEdge || !leftCladdingEdge || !rightCladdingEdge) {
            continue;
          }

          const { startCap, endCap } = claddingResult;

          const mass: WallPlaneSubdomainMiterMass = {
            miterPlane: domainPlane,
            bottom: minDatum,
            top: maxDatum,
            miterEdge: domain,
            leftCladdingEdge,
            leftCoreEdge,
            rightCoreEdge,
            rightCladdingEdge,
            startCap,
            endCap,
          };

          wallPlaneSubdomainMiterMasses.push(mass);

          const { arrayIndexU } = domain.subdomain;
          // Grid stays 1:1 per subdomain cell; keep the first half for lookups.
          if (halfIndex === 0) {
            gridRowCells[arrayIndexU] = mass;
          }
        }
      }

      planeGrid[i] = gridRowCells;
    }

    massGrids[wallPlaneHash] = {
      grid: planeGrid,
    };
  }

  return { masses: wallPlaneSubdomainMiterMasses, grids: massGrids };
}

type MiterSideEdgePair = {
  leftEdge?: PolyLine2;
  rightEdge?: PolyLine2;
  startCap: boolean;
  endCap: boolean;
};

// Miters a slab's subdomain edges through the straight-skeleton
// wavefront algorithm in ../miterSkeleton. Each input edge becomes a
// MiterSourceEdge carrying its per-side widths; shared endpoints
// (within DIST_EPSILON) weld into one MiterSourceNode so wedges form
// at junctions. After runToEnd() the per-source-edge side edges
// carry the mitered endpoints at t = 1.
//
// T-junction splits (only reachable when a freeform wall is involved — see the
// `involvesFreeformWall` gate below) produce one result entry per source-edge half so
// the through wall can emit two finish domains at the stem (matching PPG room T-joins).
// Halves are sorted by param along the input centerline.
function miterSubdomainEdges(
  wallPlaneSubdomainMiterEdges: WallPlaneSubdomainMiterEdge[],
  wallPlaneSubdomainOffsetPredicate: (input: WallPlaneSubdomainMiterEdge) => {
    leftOffset: number;
    rightOffset: number;
  },
  buildingGraph: BuildingGraph,
): Map<WallPlaneSubdomainMiterEdge, MiterSideEdgePair[]> {
  const miterResult: Map<WallPlaneSubdomainMiterEdge, MiterSideEdgePair[]> = new Map();

  if (wallPlaneSubdomainMiterEdges.length === 0) {
    return miterResult;
  }

  const source = new MiterSourceGraph();
  const nodeByKey = new Map<number, MiterSourceNode>();
  const sourceEdgeToInput = new Map<MiterSourceEdge, WallPlaneSubdomainMiterEdge>();

  for (const input of wallPlaneSubdomainMiterEdges) {
    const [sx, sy, ex, ey] = input.edge;

    if (vec2DistSquared(sx, sy, ex, ey) < DIST_EPSILON_SQUARED) {
      continue;
    }

    const a = getOrCreateMiterSourceNode(source, nodeByKey, sx, sy);
    const b = getOrCreateMiterSourceNode(source, nodeByKey, ex, ey);

    if (a === b) {
      continue;
    }

    const sedge = source.connect(a, b);

    if (!sedge || sourceEdgeToInput.has(sedge)) {
      continue;
    }

    const { leftOffset, rightOffset } = wallPlaneSubdomainOffsetPredicate(input);

    sedge.leftWidth = leftOffset;
    sedge.rightWidth = rightOffset;
    sourceEdgeToInput.set(sedge, input);
  }

  // Room-derived wall paths always form closed/connected planar-graph topology, so a
  // dangling tip landing mid-span on another centerline can only occur when an open
  // freeform wall path contributes to this batch. Gate the T-junction/side-synthesis
  // machinery on that so room-only wall generation runs the original single-result
  // path, unchanged by the freeform work below.
  const involvesFreeformWall = wallPlaneSubdomainMiterEdges.some((input) =>
    subdomainHasFreeformWallParent(input.subdomain, buildingGraph),
  );

  if (involvesFreeformWall) {
    // T-junctions: a dangling tip that lands mid-span on another centerline must
    // split that edge so three edges meet at one node before the skeleton runs.
    resolveMiterSourceTJunctions(source, sourceEdgeToInput);
  }

  const skeleton = MiterSkeletonGraph.fromSource(source);
  skeleton.runToEnd();
  const sourceNodesWithCap = new Set<MiterSourceNode>();

  for (const edge of skeleton.edges) {
    if (edge.source.kind === "cap") {
      sourceNodesWithCap.add(edge.source.sourceNode);
    }
  }

  if (!involvesFreeformWall) {
    for (const [sedge, input] of sourceEdgeToInput) {
      const side = skeleton.sourceSideEdges.get(sedge);
      if (!side) {
        continue;
      }
      const leftEdge = extractMiterSkeletonSidePolyline(side.left);
      const rightEdge = extractMiterSkeletonSidePolyline(side.right);
      if (!leftEdge && !rightEdge) {
        continue;
      }
      const startCap = sourceNodesWithCap.has(sedge.a);
      const endCap = sourceNodesWithCap.has(sedge.b);
      miterResult.set(input, [{ leftEdge, rightEdge, startCap, endCap }]);
    }
    return miterResult;
  }

  const halvesByInput = new Map<WallPlaneSubdomainMiterEdge, MiterSideEdgePair[]>();

  for (const [sedge, input] of sourceEdgeToInput) {
    const side = skeleton.sourceSideEdges.get(sedge);
    const startCap = sourceNodesWithCap.has(sedge.a);
    const endCap = sourceNodesWithCap.has(sedge.b);
    let leftEdge = side ? extractMiterSkeletonSidePolyline(side.left) : undefined;
    let rightEdge = side ? extractMiterSkeletonSidePolyline(side.right) : undefined;

    // Stem wavefront can collapse one side of a through-half; synthesize from
    // the source-edge centerline so that half still produces a wall mass.
    if (!leftEdge) {
      leftEdge = synthesizeSideEdgeFromSourceEdge(sedge, input.edge, true);
    }
    if (!rightEdge) {
      rightEdge = synthesizeSideEdgeFromSourceEdge(sedge, input.edge, false);
    }
    if (!leftEdge || !rightEdge) {
      continue;
    }

    leftEdge = orientSideEdgeToInputDirection(leftEdge, input.edge);
    rightEdge = orientSideEdgeToInputDirection(rightEdge, input.edge);

    const halves = halvesByInput.get(input);
    if (halves) {
      halves.push({ leftEdge, rightEdge, startCap, endCap });
    } else {
      halvesByInput.set(input, [{ leftEdge, rightEdge, startCap, endCap }]);
    }
  }

  for (const [input, halves] of halvesByInput) {
    halves.sort((a, b) => miterHalfSortParam(a, input.edge) - miterHalfSortParam(b, input.edge));
    miterResult.set(input, halves);
  }

  return miterResult;
}

/**
 * Ensure a miter side polyline runs in the same direction as the input centerline.
 * Skeleton left/right polylines can be anti-parallel, which breaks finish-side
 * domain checks downstream.
 */
function orientSideEdgeToInputDirection(sideEdge: PolyLine2, inputEdge: PolyLine2): PolyLine2 {
  const [sx, sy, ex, ey] = inputEdge;
  const [tx, ty] = line2Tangent(sx, sy, ex, ey);
  const startParam = vec2Dot(sideEdge[0] - sx, sideEdge[1] - sy, tx, ty);
  const endParam = vec2Dot(sideEdge[sideEdge.length - 2] - sx, sideEdge[sideEdge.length - 1] - sy, tx, ty);
  if (endParam < startParam) {
    return polyLine2ReverseCopy(sideEdge);
  }
  return sideEdge;
}

function miterHalfSortParam(half: MiterSideEdgePair, inputEdge: PolyLine2): number {
  const edge = half.leftEdge ?? half.rightEdge;
  if (!edge) {
    return 0;
  }
  const [sx, sy, ex, ey] = inputEdge;
  const [tx, ty] = line2Tangent(sx, sy, ex, ey);
  const midX = (edge[0] + edge[edge.length - 2]) / 2;
  const midY = (edge[1] + edge[edge.length - 1]) / 2;
  return vec2Dot(midX - sx, midY - sy, tx, ty);
}

/**
 * Build a parallel side edge over a source-edge half when the skeleton collapsed
 * that side (common on the stem-facing side of a T-split through wall).
 *
 * Only called from the freeform-gated branch of {@link miterSubdomainEdges} — room-only
 * wall-plane miters never produce a collapsed side needing this, since room wall paths
 * always meet as closed loops with room-derived widths, not open-ended freeform stems.
 */
function synthesizeSideEdgeFromSourceEdge(
  sedge: MiterSourceEdge,
  inputEdge: PolyLine2,
  isLeft: boolean,
): PolyLine2 | undefined {
  let sx = sedge.a.position.x;
  let sy = sedge.a.position.y;
  let ex = sedge.b.position.x;
  let ey = sedge.b.position.y;
  if (vec2DistSquared(sx, sy, ex, ey) < DIST_EPSILON_SQUARED) {
    return undefined;
  }

  const [tx, ty] = line2Tangent(inputEdge[0], inputEdge[1], inputEdge[2], inputEdge[3]);
  if (vec2Dot(ex - sx, ey - sy, tx, ty) < 0) {
    const tmpX = sx;
    const tmpY = sy;
    sx = ex;
    sy = ey;
    ex = tmpX;
    ey = tmpY;
  }

  const [nx, ny] = vec2Normal(sx, sy, ex, ey);
  const width = isLeft ? sedge.leftWidth : sedge.rightWidth;
  const sign = isLeft ? 1 : -1;
  return polyLine2New([sx + nx * width * sign, sy + ny * width * sign, ex + nx * width * sign, ey + ny * width * sign]);
}

/**
 * For each degree-1 source node whose position lies on another source edge within
 * DIST_EPSILON, split that edge (or weld to a nearby endpoint) and reconnect the
 * dangling stem so a proper T/vertex miter wedge forms.
 *
 * Room-derived wall paths always form closed/connected planar-graph topology, so a
 * degree-1 (dangling) source node can only arise when an open freeform wall path
 * contributes to the batch — this is why {@link miterSubdomainEdges} only calls this
 * when a freeform wall is involved, rather than making it a general miter step.
 */
function resolveMiterSourceTJunctions(
  source: MiterSourceGraph,
  sourceEdgeToInput: Map<MiterSourceEdge, WallPlaneSubdomainMiterEdge>,
): void {
  const tips = source.nodes.filter((node) => node.connections.length === 1);

  for (const tip of tips) {
    if (tip.connections.length !== 1) {
      continue;
    }

    const neighbor = tip.connections[0];
    const stemEdge = source.findEdge(tip, neighbor);
    if (!stemEdge) {
      continue;
    }

    const hit = nearestMiterSourceEdgeExcluding(source, tip.position, stemEdge);
    if (!hit) {
      continue;
    }

    const { edge: hitEdge, projection } = hit;
    let attachNode: MiterSourceNode | null = null;
    if (distance(projection, hitEdge.a.position) <= DIST_EPSILON) {
      attachNode = hitEdge.a;
    } else if (distance(projection, hitEdge.b.position) <= DIST_EPSILON) {
      attachNode = hitEdge.b;
    }

    if (attachNode) {
      if (attachNode === tip || attachNode === neighbor) {
        continue;
      }
      reconnectMiterStem(source, sourceEdgeToInput, stemEdge, tip, neighbor, attachNode);
      continue;
    }

    const hitInput = sourceEdgeToInput.get(hitEdge);
    const origA = hitEdge.a;
    const origB = hitEdge.b;
    const mid = source.splitEdge(origA, origB, projection);
    sourceEdgeToInput.delete(hitEdge);
    if (hitInput) {
      for (const edge of source.edges) {
        const touchesMid = edge.a === mid || edge.b === mid;
        const touchesOrig = edge.a === origA || edge.b === origA || edge.a === origB || edge.b === origB;
        if (touchesMid && touchesOrig) {
          sourceEdgeToInput.set(edge, hitInput);
        }
      }
    }

    reconnectMiterStem(source, sourceEdgeToInput, stemEdge, tip, neighbor, mid);
  }
}

function nearestMiterSourceEdgeExcluding(
  source: MiterSourceGraph,
  point: Vector2,
  excluded: MiterSourceEdge,
): { edge: MiterSourceEdge; projection: Vector2 } | null {
  let best: { edge: MiterSourceEdge; projection: Vector2 } | null = null;
  let bestDist = DIST_EPSILON;

  for (const edge of source.edges) {
    if (edge === excluded) {
      continue;
    }
    const d = distanceToSegment(point, edge.a.position, edge.b.position);
    if (d <= bestDist) {
      bestDist = d;
      best = {
        edge,
        projection: projectOntoSegment(point, edge.a.position, edge.b.position),
      };
    }
  }

  return best;
}

function reconnectMiterStem(
  source: MiterSourceGraph,
  sourceEdgeToInput: Map<MiterSourceEdge, WallPlaneSubdomainMiterEdge>,
  stemEdge: MiterSourceEdge,
  tip: MiterSourceNode,
  neighbor: MiterSourceNode,
  attachNode: MiterSourceNode,
): void {
  const input = sourceEdgeToInput.get(stemEdge);
  const leftWidth = stemEdge.leftWidth;
  const rightWidth = stemEdge.rightWidth;

  source.disconnect(tip, neighbor);
  sourceEdgeToInput.delete(stemEdge);

  if (attachNode === neighbor) {
    return;
  }

  const newStem = source.connect(attachNode, neighbor);
  if (!newStem) {
    return;
  }

  newStem.leftWidth = leftWidth;
  newStem.rightWidth = rightWidth;
  if (input) {
    sourceEdgeToInput.set(newStem, input);
  }
}

// Weld by spatial hash so two input edges that meet at a shared
// endpoint end up attached to the same MiterSourceNode.
function getOrCreateMiterSourceNode(
  source: MiterSourceGraph,
  nodeByKey: Map<number, MiterSourceNode>,
  x: number,
  y: number,
): MiterSourceNode {
  const key = hashVec2(x, y);
  const existing = nodeByKey.get(key);

  if (existing) {
    return existing;
  }

  const node = source.addNode({ x, y });
  nodeByKey.set(key, node);
  return node;
}

// Pull a PolyLine2 out of a skeleton side edge at t = 1. Returns
// undefined when the side collapsed during the run (a === b, i.e. a
// zero-length self-loop mapping marker), the endpoints are within
// float noise of each other, or any coordinate came out non-finite.
// The last case shouldn't be reachable in normal use, but warn
// rather than feed NaN into the downstream pipeline.
function extractMiterSkeletonSidePolyline(edge: MiterSkeletonEdge): PolyLine2 | undefined {
  if (edge.a === edge.b) {
    return undefined;
  }

  const ax = edge.a.position.x;
  const ay = edge.a.position.y;
  const bx = edge.b.position.x;
  const by = edge.b.position.y;

  if (!Number.isFinite(ax) || !Number.isFinite(ay) || !Number.isFinite(bx) || !Number.isFinite(by)) {
    console.warn("miter skeleton produced side edge with non-finite endpoint");
    return undefined;
  }

  /**
   * @NOTE - enable for debugging miter edge collapse
   */
  // const lsq = vec2DistSquared(ax, ay, bx, by);
  // if (lsq < DIST_EPSILON_SQUARED) {
  //   // This condition is OK. it's the result of an edge collapse -
  //   // we need to pass the collapsed point downstream to the wall edges
  //   console.warn("miter skeleton produced side edge with zero-length endpoint");
  // }

  return polyLine2New([ax, ay, bx, by]);
}
